import baseApi from './RootBaseApi';
import {
  BoatResponse,
  BoatListQueryParams,
  BoatModelResponse,
  BoatPayload,
} from '@/types/boat-management.types';
import { PaginatedApiResponse } from '@/types/api.types';

export const BoatManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBoats: builder.query<
      PaginatedApiResponse<BoatResponse[]>,
      BoatListQueryParams
    >({
      query: ({ page, limit, search, type }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        if (search) params.append('search', search);
        if (type) params.append('type', type);
        return {
          url: 'boats',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: PaginatedApiResponse<BoatResponse[]>) =>
        response,
      providesTags: [{ type: 'Boats', id: 'LIST' }],
      keepUnusedDataFor: 0,
    }),

    createBoat: builder.mutation<BoatResponse, BoatPayload>({
      query: (data) => ({
        url: 'boats',
        method: 'POST',
        data,
      }),
      transformResponse: (response: BoatResponse) => response,
      invalidatesTags: [{ type: 'Boats', id: 'LIST' }],
    }),
    updateBoat: builder.mutation<
      BoatResponse,
      { boat_id: string; data: BoatPayload }
    >({
      query: ({ boat_id, data }) => ({
        url: `boats/${boat_id}`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: BoatResponse) => response,
      invalidatesTags: [{ type: 'Boats', id: 'LIST' }],
    }),
    deleteBoat: builder.mutation<void, string>({
      query: (boat_id) => ({
        url: `boats/${boat_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Boats', id: 'LIST' }],
    }),
    getBoatModels: builder.query<
      PaginatedApiResponse<BoatModelResponse[]>,
      { page?: number; limit?: number; search?: string }
    >({
      query: ({ page, limit, search }) => {
        const params = new URLSearchParams({});
        if (page) params.append('page', String(page));
        if (limit) params.append('limit', String(limit));
        if (search) params.append('search', search);
        return {
          url: 'boat-models',
          method: 'GET',
          params,
        };
      },
      transformResponse: (
        response: PaginatedApiResponse<BoatModelResponse[]>
      ) => response,
    }),
    getBoatById: builder.query<BoatResponse, string>({
      query: (boatId) => ({
        url: `boats/${boatId}`,
        method: 'GET',
      }),
      transformResponse: (response: BoatResponse) => response,
      providesTags: (result, error, boatId) => [{ type: 'Boats', id: boatId }],
      keepUnusedDataFor: 0,
    }),

    getEligibleBoatsForInspection: builder.query<
      PaginatedApiResponse<BoatResponse[]>,
      {
        clubId: string;
        memberId: string;
        page?: number;
        limit?: number;
        search?: string;
      }
    >({
      query: ({ clubId, memberId, page, limit, search }) => {
        const params = new URLSearchParams({
          club_id: clubId,
          member_id: memberId,
        });
        if (page) params.append('page', String(page));
        if (limit) params.append('limit', String(limit));
        if (search) params.append('search', search);
        return {
          url: 'boats/eligible-for-inspection',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: PaginatedApiResponse<BoatResponse[]>) =>
        response,
      providesTags: [{ type: 'Boats', id: 'LIST' }],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetBoatsQuery,
  useGetEligibleBoatsForInspectionQuery,
  useCreateBoatMutation,
  useUpdateBoatMutation,
  useDeleteBoatMutation,
  useGetBoatModelsQuery,
  useGetBoatByIdQuery,
} = BoatManagementService;
